import { trackCapitalInteraction } from 'apis/consolidated-practice'
import type { CapitalEventType } from 'util/capital'

/** Keys with a POST in flight, so one tab cannot send the same event twice. */
const inFlightCapitalEvents = new Set<string>()

/**
 * Same as {@link trackCapitalInteraction} but fires at most once per
 * (practice, `dedupeKey`), across tabs and tab restarts. Use for view-type
 * events; per-action events should call `trackCapitalInteraction` directly.
 *
 * The marker lands only once the POST resolves, so a failure retries on the next
 * page view rather than being lost. Until it lands, `inFlightCapitalEvents` holds
 * the key so this tab cannot send twice; a second tab firing inside that window
 * still can. Dedupe is client-side only, so reporting counts distinct practices
 * per offer rather than trusting the row count.
 *
 * @param consolidatedPracticeId - Consolidated practice ID.
 * @param eventType - Event from {@link CapitalEventType}.
 * @param dedupeKey - Scope the event is unique within, e.g. the offer id.
 *   Events for a new offer send again.
 * @param metadata - Extra event fields.
 */
export const trackCapitalInteractionOnce = (
	consolidatedPracticeId: string,
	eventType: CapitalEventType,
	dedupeKey: string,
	metadata?: Record<string, unknown>
): void => {
	const key = `capital_evt:${consolidatedPracticeId}:${eventType}:${dedupeKey}`
	if (inFlightCapitalEvents.has(key)) return
	try {
		// Storage blocked means no marker can be written either, so sending would fire
		// the event on every page view — skip rather than over-count.
		if (window.localStorage.getItem(key)) return
	} catch {
		return
	}

	inFlightCapitalEvents.add(key)
	trackCapitalInteraction(consolidatedPracticeId, eventType, metadata).then((sent) => {
		inFlightCapitalEvents.delete(key)
		if (!sent) return
		try {
			window.localStorage.setItem(key, '1')
		} catch {
			// see above
		}
	})
}
